import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Landmark, Loader2, RefreshCw } from 'lucide-react';

const TapPayouts = () => {
  const [loading, setLoading] = useState(false);
  const [payouts, setPayouts] = useState(null);
  const [destinations, setDestinations] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, d] = await Promise.all([
        api.get('/tap/payouts').then((r) => r.data).catch((e) => ({ status: 'error', error: e.message })),
        api.get('/tap/destinations').then((r) => r.data).catch((e) => ({ status: 'error', error: e.message })),
      ]);
      setPayouts(p);
      setDestinations(d);
    } catch (e) {
      toast.error('Failed to load Tap settlement report');
    } finally {
      setLoading(false);
    }
  };

  const payoutRows = (() => {
    const d = payouts?.data;
    if (!d) return [];
    if (Array.isArray(d?.payouts)) return d.payouts;
    if (Array.isArray(d)) return d;
    return [];
  })();

  const destRows = Array.isArray(destinations?.data?.destinations) ? destinations.data.destinations : [];

  return (
    <Card className="p-6" data-testid="tap-payouts-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Landmark className="w-5 h-5 text-teal-600" />
          <h3 className="text-xl font-semibold">Auto-Settlements (Bank Payouts)</h3>
          <Badge variant="outline" className="text-teal-600 border-teal-300">Reporting</Badge>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="tap-payouts-refresh-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="ml-1">Load report</span>
        </Button>
      </div>
      <p className="text-[11px] text-slate-500 mb-4">
        Tap settles captured charges to your registered bank account <strong>automatically on Tap&apos;s payout schedule</strong> — there is no manual &quot;send&quot; step. This report shows those settlements exactly as Tap reports them.
      </p>

      {payouts && payouts.status !== 'success' && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3" data-testid="tap-payouts-status">
          Tap payout API returned {payouts.http_code || ''}: {typeof payouts.error === 'string' ? payouts.error : JSON.stringify(payouts.data || payouts.error || '')}
        </div>
      )}

      {payoutRows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="tap-payouts-table">
            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase">
              <tr>
                <th className="text-left px-2 py-1.5">ID</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-right px-2 py-1.5">Amount</th>
                <th className="text-left px-2 py-1.5">Currency</th>
                <th className="text-left px-2 py-1.5">Date</th>
              </tr>
            </thead>
            <tbody>
              {payoutRows.map((r, i) => (
                <tr key={r.id || i} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 font-mono text-[10px]">{r.id || '-'}</td>
                  <td className="px-2 py-1.5">{r.status || '-'}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.amount ?? '-'}</td>
                  <td className="px-2 py-1.5">{r.currency || '-'}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{(r.created || r.date || '').toString().slice(0, 19)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : payouts ? (
        <p className="text-sm text-slate-500" data-testid="tap-payouts-empty">No settlements reported yet. Once Tap captures charges and runs its payout cycle, settlements will appear here.</p>
      ) : (
        <p className="text-sm text-slate-400">Click &quot;Load report&quot; to fetch the latest Tap settlement report.</p>
      )}

      {destRows.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-slate-700 mb-1">Registered payout destinations</p>
          <ul className="text-xs text-slate-600 list-disc pl-5" data-testid="tap-destinations-list">
            {destRows.map((d, i) => (
              <li key={d.id || i}>{d.display_name || d.name || d.id}</li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default TapPayouts;
